/**
 * Frontend mirror of the backend module catalog (api/src/events/module-catalog.ts).
 * Keep the KEYS in sync. Used by the Choose-Modules screen and nav gating.
 */

export interface ModuleDef {
  key: string;
  label: string;
  description: string;
  icon: string;
  dependsOn?: string[];
  /** Base modules are always included in a tenant's ceiling (can't be removed). */
  isBase?: boolean;
}

export const MODULE_CATALOG: ModuleDef[] = [
  { key: 'guests', label: 'Guest List', icon: '🎟️', description: 'Attendees, registrations, and the guest directory.', isBase: true },
  { key: 'ticketing', label: 'Ticketing & Orders', icon: '💳', description: 'Sell tickets, manage orders, tiers, and inventory.' },
  { key: 'seating', label: 'Seating & Tables', icon: '🪑', description: 'Zones, tables, floorplan, and the AI seating engine.', dependsOn: ['guests'], isBase: true },
  { key: 'checkin', label: 'Check-In', icon: '✅', description: 'On-site check-in, packet pickup, and QR scanning.', dependsOn: ['guests'] },
  { key: 'agenda', label: 'Agenda & Sessions', icon: '📅', description: 'Schedule, sessions, and program for attendees.' },
  { key: 'vendors', label: 'Vendors & Booths', icon: '🏪', description: 'Vendor registration, booth map, and showcase.' },
  { key: 'comms', label: 'Communications', icon: '📢', description: 'Announcements and broadcasts to attendees.' },
  { key: 'website', label: 'Event Experience', icon: '🌐', description: 'A configurable attendee hub (schedule, ticket, info).' },
  { key: 'giving', label: 'Giving', icon: '💚', description: 'Year-round fundraising campaigns, donors, and goals (tenant pillar).' },
];

/** Base module keys — always on in a tenant ceiling. */
export const BASE_MODULE_KEYS: string[] = MODULE_CATALOG.filter((m) => m.isBase).map((m) => m.key);

export interface EventTypeDef {
  key: string;
  label: string;
  icon: string;
  description: string;
  modules: string[];
}

/** Event-type presets for the create-event flow (mirror of backend). */
export const EVENT_TYPES: EventTypeDef[] = [
  { key: 'full', label: 'Everything', icon: '✨', description: 'Enable every module your plan allows.', modules: [] },
  { key: 'conference', label: 'Conference', icon: '🎤', description: 'Sessions, ticketing, and check-in.', modules: ['guests', 'ticketing', 'agenda', 'checkin', 'comms', 'website'] },
  { key: 'reunion', label: 'Reunion', icon: '🎭', description: 'Seating, guests, and check-in.', modules: ['guests', 'seating', 'checkin', 'comms', 'website'] },
  { key: 'fundraiser', label: 'Fundraiser / Gala', icon: '🥂', description: 'Ticketing, seating, and check-in.', modules: ['guests', 'ticketing', 'seating', 'checkin', 'comms', 'website'] },
  { key: 'festival', label: 'Festival', icon: '🎪', description: 'Vendors, ticketing, and agenda.', modules: ['guests', 'ticketing', 'vendors', 'agenda', 'comms', 'website'] },
  { key: 'community', label: 'Community Event', icon: '🤝', description: 'Guests, agenda, and communications.', modules: ['guests', 'agenda', 'comms', 'website'] },
  { key: 'private', label: 'Private Party', icon: '🎉', description: 'Invite-only — guests & seating, no tickets or revenue.', modules: ['guests', 'seating', 'checkin', 'comms', 'website'] },
];
